import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSettings } from '../../context/SettingsContext';
import {
  createDocumentProfile,
  deleteDocumentProfile,
  listDocumentProfiles,
  updateDocumentProfile,
  listDocumentNumberSeries,
  saveDocumentNumberSeries,
} from '../../services/documentProfileService';
import MediaAssetPicker from '../settings/MediaAssetPicker';
import WgiymEditor from '../ui/WgiymEditor';
import api from '../../services/api';
import { mediaUrl } from '../media/MediaAssetLibrary';
import { formatDate } from '../../utils/dateFormatter';
import ConfirmDialog from '../ui/ConfirmDialog';
import NotificationSnackbar from '../ui/NotificationSnackbar';
import {
  CrmButton,
  CrmInput,
  CrmSelect,
  CrmSwitch,
  CrmTabs,
} from '../ui';
import './document-template-settings.scss';

const ACTIVE_VALUES = new Set([true, 1, '1', 'true']);
const isActive = (row) => row?.is_active == null || ACTIVE_VALUES.has(row.is_active);
const bool = (value, fallback = false) => value == null ? fallback : ACTIVE_VALUES.has(value);
const nullableId = (value) => value ? Number(value) : null;

const EMPTY_PROFILE = (documentType, templateKey = 'default') => ({
  id: null,
  name: '',
  document_type: documentType,
  legal_entity_id: '',
  brand_id: '',
  business_unit_id: '',
  storefront_id: '',
  template_key: templateKey,
  template_version: '1',
  logo_media_asset_id: '',
  brand_display_name: '',
  support_phone: '',
  support_email: '',
  website: '',
  terms_and_conditions: '',
  payment_instructions: '',
  cover_letter: '',
  show_cover_letter: false,
  notes: '',
  page_size: 'A4',
  orientation: 'portrait',
  show_page_numbers: false,
  show_logo: true,
  show_tax_breakdown: true,
  is_default: false,
  priority: 0,
  enabled: true,
  header_config: {},
  footer_config: {},
  margins: {},
  presentation_config: {},
  action_settings: {},
});

const normalizeProfile = (row, documentType, defaultTemplate) => ({
  ...EMPTY_PROFILE(documentType, defaultTemplate),
  ...(row || {}),
  id: row?.id || null,
  legal_entity_id: row?.legal_entity_id || '',
  brand_id: row?.brand_id || '',
  business_unit_id: row?.business_unit_id || '',
  storefront_id: row?.storefront_id || '',
  logo_media_asset_id: row?.logo_media_asset_id || '',
  enabled: bool(row?.enabled, true),
  is_default: bool(row?.is_default, false),
  show_logo: bool(row?.show_logo, true),
  show_tax_breakdown: bool(row?.show_tax_breakdown, true),
  show_page_numbers: bool(row?.show_page_numbers, false),
  show_cover_letter: bool(row?.show_cover_letter, false),
  priority: Number(row?.priority || 0),
});

const relationName = (rows, id, fallback = 'All') => {
  if (!id) return fallback;
  const row = (rows || []).find((item) => Number(item.id) === Number(id));
  return row?.name || row?.legal_name || row?.trade_name || row?.code || 'Unknown';
};

const defaultTemplateFor = (documentType) => documentType === 'QUOTATION' ? 'general' : 'default';

const templateOptionsFor = (documentType) => {
  if (documentType === 'QUOTATION') {
    return [
      ['general', 'General'],
      ['minimal', 'Minimal'],
      ['classic', 'Classic'],
      ['modern', 'Modern'],
    ];
  }
  return [['default', 'Default']];
};

const profileScopeLabel = (profile, catalog) => {
  if (profile.storefront_id) return `Storefront · ${relationName(catalog.storefronts, profile.storefront_id)}`;
  if (profile.business_unit_id) return `Business unit · ${relationName(catalog.business_units, profile.business_unit_id)}`;
  if (profile.brand_id) return `Brand · ${relationName(catalog.brands, profile.brand_id)}`;
  if (profile.legal_entity_id) return `Legal entity · ${relationName(catalog.legal_entities, profile.legal_entity_id)}`;
  return 'Global';
};

function OptionList({ rows, labelKey = 'name' }) {
  return (rows || []).filter(isActive).map((row) => (
    <option key={row.id} value={row.id}>{row[labelKey] || row.name || row.legal_name || row.code}</option>
  ));
}

export default function DocumentTemplateSettings({
  title,
  description,
  documentTypes,
}) {
  const { commercialCatalog, reloadOperationalContext, settings } = useSettings();
  const catalog = commercialCatalog || {};
  const dateFormat = settings?.date_format || 'DD/MM/YYYY';
  const [activeType, setActiveType] = useState(documentTypes[0]?.value || 'QUOTATION');
  const [profiles, setProfiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(() => EMPTY_PROFILE(activeType, defaultTemplateFor(activeType)));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [previewLogoUrl, setPreviewLogoUrl] = useState('');
  const [previewPage, setPreviewPage] = useState(0);
  const [quotationHtml, setQuotationHtml] = useState('');
  const [quotationPreviewError, setQuotationPreviewError] = useState('');
  const [numberSeries, setNumberSeries] = useState([]);
  const [numbering, setNumbering] = useState({ id: null, prefix: activeType === 'QUOTATION' ? 'QT' : activeType === 'WORK_ORDER' ? 'WO' : activeType === 'PROFORMA_INVOICE' ? 'PI' : 'INV', number_format: '{prefix}/{year}/{seq}', numbering_mode: 'continuous', sequence_start: 1, enabled: true, is_default: true });
  const [savingNumbering, setSavingNumbering] = useState(false);

  const activeTypeMeta = useMemo(
    () => documentTypes.find((item) => item.value === activeType) || documentTypes[0],
    [activeType, documentTypes]
  );

  const showNotification = (message, severity = 'success') => setNotification({ open: true, message, severity });

  const loadProfiles = useCallback(async (documentType, preferredId = null) => {
    setLoading(true);
    try {
      const rows = await listDocumentProfiles(documentType);
      const normalized = Array.isArray(rows) ? rows : rows?.items || [];
      setProfiles(normalized);
      const preferred = preferredId ? normalized.find((row) => Number(row.id) === Number(preferredId)) : null;
      const next = preferred || normalized[0] || null;
      setSelectedId(next?.id || null);
      setForm(normalizeProfile(next, documentType, defaultTemplateFor(documentType)));
    } catch (error) {
      showNotification(error?.response?.data?.error || 'Failed to load document templates.', 'error');
      setProfiles([]);
      setSelectedId(null);
      setForm(EMPTY_PROFILE(documentType, defaultTemplateFor(documentType)));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNumbering = useCallback(async (documentType) => {
    try {
      const rows = await listDocumentNumberSeries(documentType);
      setNumberSeries(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setNumberSeries([]);
    }
  }, []);

  useEffect(() => {
    loadProfiles(activeType);
    loadNumbering(activeType);
  }, [activeType, loadProfiles, loadNumbering]);

  useEffect(() => {
    if (!commercialCatalog) reloadOperationalContext?.();
  }, [commercialCatalog, reloadOperationalContext]);

  useEffect(() => {
    let alive = true;
    const id = form.logo_media_asset_id;
    if (!id) { setPreviewLogoUrl(''); return undefined; }
    api.get(`/media-assets/${id}`).then(({ data }) => { if (alive) setPreviewLogoUrl(data?.url || ''); }).catch(() => { if (alive) setPreviewLogoUrl(''); });
    return () => { alive = false; };
  }, [form.logo_media_asset_id]);

  useEffect(() => {
    const same = (row) => ['legal_entity_id','brand_id','business_unit_id','storefront_id'].every((key) => Number(row?.[key] || 0) === Number(form?.[key] || 0));
    const selected = numberSeries.find(same) || numberSeries.find((row) => row.is_default) || numberSeries[0] || null;
    const prefix = activeType === 'QUOTATION' ? 'QT' : activeType === 'WORK_ORDER' ? 'WO' : activeType === 'PROFORMA_INVOICE' ? 'PI' : 'INV';
    setNumbering(selected ? { ...selected } : { id: null, prefix, number_format: '{prefix}/{year}/{seq}', numbering_mode: 'continuous', sequence_start: 1, enabled: true, is_default: true });
  }, [numberSeries, activeType, form.legal_entity_id, form.brand_id, form.business_unit_id, form.storefront_id]);

  const selectProfile = (profile) => {
    setSelectedId(profile.id);
    setForm(normalizeProfile(profile, activeType, defaultTemplateFor(activeType)));
  };

  const startNew = () => {
    setSelectedId(null);
    setForm(EMPTY_PROFILE(activeType, defaultTemplateFor(activeType)));
  };

  const duplicateCurrent = () => {
    setSelectedId(null);
    setForm((current) => ({
      ...current,
      id: null,
      profile_key: undefined,
      name: `${current.name || activeTypeMeta?.label || 'Document'} Copy`,
      is_default: false,
      priority: Number(current.priority || 0),
    }));
  };

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    if (!String(form.name || '').trim()) {
      showNotification('Template name is required.', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        document_type: activeType,
        name: String(form.name).trim(),
        legal_entity_id: nullableId(form.legal_entity_id),
        brand_id: nullableId(form.brand_id),
        business_unit_id: nullableId(form.business_unit_id),
        storefront_id: nullableId(form.storefront_id),
        logo_media_asset_id: nullableId(form.logo_media_asset_id),
        priority: Number(form.priority || 0),
        enabled: Boolean(form.enabled),
        is_default: Boolean(form.is_default),
        show_logo: Boolean(form.show_logo),
        show_tax_breakdown: Boolean(form.show_tax_breakdown),
        show_page_numbers: Boolean(form.show_page_numbers),
        show_cover_letter: Boolean(form.show_cover_letter),
      };
      const saved = selectedId
        ? await updateDocumentProfile(selectedId, payload)
        : await createDocumentProfile(payload);
      showNotification(`${activeTypeMeta?.label || 'Document'} template saved.`);
      await loadProfiles(activeType, saved?.id || selectedId);
    } catch (error) {
      showNotification(error?.response?.data?.error || 'Failed to save document template.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveNumbering = async () => {
    setSavingNumbering(true);
    try {
      const saved = await saveDocumentNumberSeries({
        ...numbering,
        document_type: activeType,
        name: numbering.name || `${activeTypeMeta?.label || activeType} Numbering`,
        code: numbering.code || 'DEFAULT',
        legal_entity_id: nullableId(form.legal_entity_id) || (activeType === 'TAX_INVOICE' ? nullableId(numbering.legal_entity_id) || nullableId((catalog.legal_entities || []).find(isActive)?.id) : null),
        brand_id: activeType === 'TAX_INVOICE' ? null : nullableId(form.brand_id),
        business_unit_id: activeType === 'TAX_INVOICE' ? null : nullableId(form.business_unit_id),
        storefront_id: activeType === 'TAX_INVOICE' ? null : nullableId(form.storefront_id),
        sequence_start: Number(numbering.sequence_start || 1),
        enabled: numbering.enabled !== false,
        is_default: numbering.is_default !== false,
      });
      setNumbering(saved || numbering);
      await loadNumbering(activeType);
      showNotification('Numbering settings saved. Existing issued document numbers were not changed.');
    } catch (error) {
      showNotification(error?.response?.data?.error || 'Failed to save numbering settings.', 'error');
    } finally { setSavingNumbering(false); }
  };

  const remove = async () => {
    if (!selectedId) return;
    setDeleting(true);
    try {
      await deleteDocumentProfile(selectedId);
      showNotification('Template deleted.');
      setDeleteOpen(false);
      await loadProfiles(activeType);
    } catch (error) {
      showNotification(error?.response?.data?.error || 'Failed to delete document template.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const scopeLabel = profileScopeLabel(form, catalog);
  const previewName = form.brand_display_name || relationName(catalog.brands, form.brand_id, '') || 'Your Brand';
  const templateClass = `is-${String(form.template_key || 'default').toLowerCase()}`;
  const issuer = (catalog.legal_entities || []).find((row) => Number(row.id) === Number(form.legal_entity_id || 0)) || {};
  const issuerAddress = (() => { const a = issuer.registered_address; if (!a) return ''; if (typeof a === 'string') { try { const p = JSON.parse(a); return p.address || [p.line1,p.line2,p.city,p.state,p.pincode].filter(Boolean).join(', '); } catch { return a; } } return a.address || [a.line1,a.line2,a.city,a.state,a.pincode].filter(Boolean).join(', '); })();
  const previewPages = useMemo(() => {
    const pages = [];
    const plainLength = (value) => String(value || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length;
    if (form.show_cover_letter && plainLength(form.cover_letter)) pages.push({ type: 'cover' });
    const sections = [
      { key: 'payment', label: 'PAYMENT INSTRUCTIONS', html: form.payment_instructions || '', length: plainLength(form.payment_instructions) },
      { key: 'terms', label: 'TERMS & CONDITIONS', html: form.terms_and_conditions || '', length: plainLength(form.terms_and_conditions) },
      { key: 'notes', label: 'NOTES', html: form.notes || '', length: plainLength(form.notes) },
    ].filter((section) => section.length > 0);
    const richLength = sections.reduce((sum, section) => sum + section.length, 0);
    pages.push({ type: 'document', includeRichContent: richLength <= 1200 });
    if (richLength > 1200) {
      let group = [];
      let groupLength = 0;
      sections.forEach((section) => {
        if (group.length && groupLength + section.length > 1200) {
          pages.push({ type: 'content', sections: group });
          group = [];
          groupLength = 0;
        }
        group.push(section);
        groupLength += section.length;
      });
      if (group.length) pages.push({ type: 'content', sections: group });
    }
    return pages;
  }, [form.show_cover_letter, form.cover_letter, form.terms_and_conditions, form.payment_instructions, form.notes]);
  useEffect(() => { if (previewPage >= previewPages.length) setPreviewPage(Math.max(0, previewPages.length - 1)); }, [previewPage, previewPages.length]);
  useEffect(() => {
    if (activeType !== 'QUOTATION') return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post('/quotations/draft-preview/html', {
          profile: form,
          context: { legal_entity_id: form.legal_entity_id || null, brand_id: form.brand_id || null, business_unit_id: form.business_unit_id || null, storefront_id: form.storefront_id || null },
          quotation: { company_name: 'Sample Customer Pvt Ltd', billing_city: 'Ahmedabad', billing_state: 'Gujarat', quotation_date: '2026-09-12', quotation_number: 'Draft' },
          items: [
            { product_name: 'Product / Service', quantity: 2, selling_price: 12000, product: { description: 'Description under the item name' } },
            { product_name: 'Additional Item', quantity: 1, selling_price: 8000 },
          ],
          gstPricingMode: 'EXCLUSIVE',
        }, { responseType: 'text' });
        if (active) { setQuotationHtml(data); setQuotationPreviewError(''); }
      } catch (error) {
        if (active) { setQuotationHtml(''); setQuotationPreviewError(error?.response?.data?.error || 'Unable to render template preview.'); }
      }
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [activeType, form]);


  return (
    <div className="document-template-settings">
      <header className="document-template-settings__header">
        <div>
          <span className="document-template-settings__eyebrow">Document Engine</span>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        <CrmButton variant="primary" startIcon={<AddIcon fontSize="small" />} onClick={startNew}>New Template</CrmButton>
      </header>

      <CrmTabs
        className="document-template-settings__type-tabs"
        items={documentTypes.map((type) => ({ id: type.value, label: type.label }))}
        activeId={activeType}
        onChange={setActiveType}
        ariaLabel="Document type"
      />

      <div className="document-template-settings__layout">
        <aside className="document-template-settings__profiles">
          <div className="document-template-settings__profiles-head">
            <div>
              <strong>{activeTypeMeta?.label} templates</strong>
              <span>{profiles.length} configured</span>
            </div>
          </div>

          {loading ? (
            <div className="document-template-settings__placeholder">Loading templates…</div>
          ) : profiles.length === 0 ? (
            <button type="button" className="document-template-settings__empty" onClick={startNew}>
              <DescriptionOutlinedIcon />
              <strong>No templates yet</strong>
              <span>Create the first scoped template.</span>
            </button>
          ) : (
            <div className="document-template-settings__profile-list">
              {profiles.map((profile) => (
                <button
                  key={profile.id}
                  type="button"
                  className={`document-template-settings__profile ${Number(selectedId) === Number(profile.id) ? 'is-active' : ''}`}
                  onClick={() => selectProfile(profile)}
                >
                  <span className="document-template-settings__profile-icon"><DescriptionOutlinedIcon fontSize="small" /></span>
                  <span className="document-template-settings__profile-copy">
                    <strong>{profile.name}</strong>
                    <small>{profileScopeLabel(profile, catalog)}</small>
                  </span>
                  <span className={`document-template-settings__status ${bool(profile.enabled, true) ? 'is-on' : ''}`}>
                    {bool(profile.enabled, true) ? 'Active' : 'Off'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </aside>

        <main className="document-template-settings__editor">
          <div className="document-template-settings__editor-toolbar">
            <div>
              <strong>{selectedId ? form.name || 'Template' : `New ${activeTypeMeta?.label || 'document'} template`}</strong>
              <span>{scopeLabel}{form.is_default ? ' · Default' : ''}</span>
            </div>
            <div className="document-template-settings__toolbar-actions">
              {selectedId ? <CrmButton variant="secondary" startIcon={<ContentCopyOutlinedIcon fontSize="small" />} onClick={duplicateCurrent}>Duplicate</CrmButton> : null}
              {selectedId ? <CrmButton variant="danger" startIcon={<DeleteOutlineIcon fontSize="small" />} onClick={() => setDeleteOpen(true)}>Delete</CrmButton> : null}
              <CrmButton variant="primary" loading={saving} startIcon={<SaveOutlinedIcon fontSize="small" />} onClick={save}>Save Template</CrmButton>
            </div>
          </div>

          <div className="document-template-settings__editor-grid">
            <section className="document-template-settings__form-column">
              <div className="document-template-card">
                <div className="document-template-card__head">
                  <div><h2>Template identity</h2><p>Name, scope and precedence in the new document architecture.</p></div>
                  {form.enabled ? <span className="document-template-card__healthy"><CheckCircleOutlineIcon fontSize="small" /> Enabled</span> : null}
                </div>
                <div className="document-template-form-grid">
                  <CrmInput label="Template Name" required value={form.name} onChange={(event) => update('name', event.target.value)} placeholder={`e.g. ${activeTypeMeta?.label} · JDH Online`} />
                  <CrmInput label="Priority" type="number" value={form.priority} onChange={(event) => update('priority', Number(event.target.value || 0))} helperText="Higher priority wins when more than one profile matches." />
                  <CrmSelect label="Template Style" value={form.template_key} onChange={(event) => update('template_key', event.target.value)}>
                    {templateOptionsFor(activeType).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </CrmSelect>
                  <CrmSelect label="Legal Entity" value={form.legal_entity_id} onChange={(event) => update('legal_entity_id', event.target.value)}>
                    <option value="">All legal entities</option>
                    <OptionList rows={catalog.legal_entities} labelKey="legal_name" />
                  </CrmSelect>
                  <CrmSelect label="Brand" value={form.brand_id} onChange={(event) => update('brand_id', event.target.value)}>
                    <option value="">All brands</option>
                    <OptionList rows={catalog.brands} />
                  </CrmSelect>
                  <CrmSelect label="Business Unit" value={form.business_unit_id} onChange={(event) => update('business_unit_id', event.target.value)}>
                    <option value="">All business units</option>
                    <OptionList rows={catalog.business_units} />
                  </CrmSelect>
                  <CrmSelect label="Storefront" helperText="For manually created CRM quotations, leave this as No storefront. Only choose a storefront when that storefront truly owns this document profile." value={form.storefront_id} onChange={(event) => update('storefront_id', event.target.value)}>
                    <option value="">No storefront · Manual CRM / global</option>
                    <OptionList rows={catalog.storefronts} />
                  </CrmSelect>
                  {!form.storefront_id && activeType === 'QUOTATION' ? <div className="document-template-settings__manual-scope"><strong>Manual CRM quotation profile</strong><span>This template does not require a storefront. It can be selected directly during manual quotation creation and resolves from the selected legal entity / brand / business unit scope.</span></div> : null}
                </div>
                <div className="document-template-switches">
                  <CrmSwitch label="Enabled" helperText="Only enabled profiles participate in document resolution." checked={Boolean(form.enabled)} onChange={(event) => update('enabled', event.target.checked)} />
                  <CrmSwitch label="Default for this scope" helperText="Used as the preferred profile when scope specificity is equal." checked={Boolean(form.is_default)} onChange={(event) => update('is_default', event.target.checked)} />
                </div>
              </div>

              <div className="document-template-card">
                <div className="document-template-card__head"><div><h2>Brand & contact</h2><p>Presentation identity printed on generated documents.</p></div></div>
                <div className="document-template-form-grid">
                  <CrmInput label="Display Brand Name" value={form.brand_display_name || ''} onChange={(event) => update('brand_display_name', event.target.value)} placeholder="Uses scoped brand/legal entity when blank" />
                  <CrmInput label="Website" value={form.website || ''} onChange={(event) => update('website', event.target.value)} placeholder="https://…" />
                  <CrmInput label="Support Phone" value={form.support_phone || ''} onChange={(event) => update('support_phone', event.target.value)} />
                  <CrmInput label="Support Email" type="email" value={form.support_email || ''} onChange={(event) => update('support_email', event.target.value)} />
                </div>
                <MediaAssetPicker
                  label="Document Logo"
                  mediaType="IMAGE"
                  storefrontId={form.storefront_id || undefined}
                  value={form.logo_media_asset_id || ''}
                  onChange={(value, asset) => { update('logo_media_asset_id', value || ''); setPreviewLogoUrl(asset?.url || ''); }}
                  helperText="Uses the scoped brand or legal identity logo when no override is selected."
                />
              </div>

              <div className="document-template-card">
                <div className="document-template-card__head"><div><h2>Document content</h2><p>Reusable legal and payment content resolved with the template.</p></div></div>
                <div className="document-template-form-stack">
                  <label className="document-rich-field"><span>Terms & Conditions</span><WgiymEditor value={form.terms_and_conditions || ''} onChange={(value) => update('terms_and_conditions', value)} /></label>
                  <label className="document-rich-field"><span>Payment Instructions</span><WgiymEditor value={form.payment_instructions || ''} onChange={(value) => update('payment_instructions', value)} /></label>
                  <div className="document-cover-letter-head"><CrmSwitch label="Use cover letter" helperText="When enabled, the cover letter is rendered as the first page before the document." checked={Boolean(form.show_cover_letter)} onChange={(event) => update('show_cover_letter', event.target.checked)} /></div>
                  {form.show_cover_letter ? <label className="document-rich-field"><span>Cover Letter</span><WgiymEditor value={form.cover_letter || ''} onChange={(value) => update('cover_letter', value)} placeholder="Write the first-page cover letter…" /></label> : null}
                  <label className="document-rich-field"><span>Footer Notes</span><WgiymEditor value={form.notes || ''} onChange={(value) => update('notes', value)} /></label>
                </div>
              </div>

              <div className="document-template-card">
                <div className="document-template-card__head"><div><h2>Numbering</h2><p>Number series used when a new {activeTypeMeta?.label?.toLowerCase()} is issued. Existing numbers never change.</p></div><CrmButton variant="secondary" loading={savingNumbering} onClick={saveNumbering}>Save Numbering</CrmButton></div>
                <div className="document-template-form-grid">
                  <CrmInput label="Prefix" value={numbering.prefix || ''} onChange={(event) => setNumbering((current) => ({ ...current, prefix: event.target.value }))} />
                  <CrmSelect label="Reset Sequence" value={numbering.numbering_mode || 'continuous'} onChange={(event) => setNumbering((current) => ({ ...current, numbering_mode: event.target.value }))}><option value="continuous">Continuous</option><option value="yearly">Yearly</option><option value="monthly">Monthly</option></CrmSelect>
                  <CrmInput label="Number Format" value={numbering.number_format || ''} onChange={(event) => setNumbering((current) => ({ ...current, number_format: event.target.value }))} helperText="Tokens: {prefix}, {year}, {month}, {seq}" />
                  <CrmInput label="Sequence Starts At" type="number" min="1" value={numbering.sequence_start || 1} onChange={(event) => setNumbering((current) => ({ ...current, sequence_start: Number(event.target.value || 1) }))} />
                </div>
                <div className="document-template-settings__number-preview">Next-number pattern preview: <strong>{String(numbering.number_format || '{prefix}/{year}/{seq}').replace('{prefix}', numbering.prefix || 'DOC').replace('{year}', '2026').replace('{month}', '09').replace('{seq}', String(numbering.sequence_start || 1).padStart(4, '0'))}</strong></div>
              </div>

              <div className="document-template-card">
                <div className="document-template-card__head"><div><h2>Output options</h2><p>Portable presentation flags stored on the document profile.</p></div></div>
                <div className="document-template-form-grid">
                  <CrmSelect label="Page Size" value={form.page_size || 'A4'} onChange={(event) => update('page_size', event.target.value)}>
                    <option value="A4">A4</option>
                    <option value="LETTER">Letter</option>
                  </CrmSelect>
                  <CrmSelect label="Orientation" value={form.orientation || 'portrait'} onChange={(event) => update('orientation', event.target.value)}>
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </CrmSelect>
                </div>
                <div className="document-template-switches">
                  <CrmSwitch label="Show logo" checked={Boolean(form.show_logo)} onChange={(event) => update('show_logo', event.target.checked)} />
                  <CrmSwitch label="Show tax breakdown" checked={Boolean(form.show_tax_breakdown)} onChange={(event) => update('show_tax_breakdown', event.target.checked)} />
                  <CrmSwitch label="Show page numbers" checked={Boolean(form.show_page_numbers)} onChange={(event) => update('show_page_numbers', event.target.checked)} />
                </div>
              </div>
            </section>

            <aside className="document-template-settings__preview-column">
              <div className="document-template-settings__preview-sticky">
                <div className="document-template-settings__preview-head">
                  <div><strong>Live Preview</strong><span>Representative layout preview</span></div>
                  <span>{activeTypeMeta?.label}</span>
                </div>
                {activeType === 'QUOTATION' ? (
                  quotationHtml ? <iframe className={`document-template-settings__html-preview is-${String(form.page_size || 'A4').toLowerCase()} ${String(form.orientation || '').toLowerCase() === 'landscape' ? 'is-landscape' : ''}`} title="Quotation template preview" srcDoc={quotationHtml} sandbox="allow-same-origin" />
                    : <div className="document-template-settings__preview-message">{quotationPreviewError || 'Rendering quotation template?'}</div>
                ) : <>
                <div className="document-preview-nav">
                  <CrmButton variant="tertiary" size="small" disabled={previewPage <= 0} onClick={() => setPreviewPage((p) => Math.max(0, p - 1))}>Previous</CrmButton>
                  <span>Page {previewPage + 1} of {previewPages.length}</span>
                  <CrmButton variant="tertiary" size="small" disabled={previewPage >= previewPages.length - 1} onClick={() => setPreviewPage((p) => Math.min(previewPages.length - 1, p + 1))}>Next</CrmButton>
                </div>
                <div className={`document-preview ${templateClass} is-${String(form.orientation || 'portrait').toLowerCase()} is-${String(form.page_size || 'A4').toLowerCase()}`}>
                  {previewPages[previewPage]?.type === 'cover' ? <>
                    <div className="document-preview__topline" />
                    <div className="document-preview__head">
                      <div className="document-preview__logo">{form.show_logo && previewLogoUrl ? <img src={mediaUrl(previewLogoUrl)} alt="Document logo" /> : form.show_logo ? 'LOGO' : ''}</div>
                      <div className="document-preview__brand"><strong>{previewName}</strong><span>{issuer.legal_name || ''}</span></div>
                    </div>
                    <div className="document-preview__cover"><strong>{activeTypeMeta?.label}</strong><span># {String(numbering.number_format || '{prefix}/{year}/{seq}').replace('{prefix}', numbering.prefix || 'DOC').replace('{year}', '2026').replace('{month}', '09').replace('{seq}', '0001')}</span><div dangerouslySetInnerHTML={{ __html: form.cover_letter || '' }} /></div>
                  </> : previewPages[previewPage]?.type === 'content' ? <>
                    <div className="document-preview__topline" /><div className="document-preview__content-page"><strong>Document Content</strong>{(previewPages[previewPage]?.sections || []).map((section) => <section key={section.key}><small>{section.label}</small><div dangerouslySetInnerHTML={{ __html: section.html }} /></section>)}</div>
                  </> : <>
                    <div className="document-preview__topline" />
                    <div className="document-preview__head">
                      <div className="document-preview__logo">{form.show_logo && previewLogoUrl ? <img src={mediaUrl(previewLogoUrl)} alt="Document logo" /> : form.show_logo ? 'LOGO' : ''}</div>
                      <div className="document-preview__brand"><strong>{previewName}</strong><span>{issuer.legal_name && issuer.legal_name !== previewName ? `Legal seller: ${issuer.legal_name}` : ''}</span><span>{issuerAddress || 'Issuer registered address'}</span><span>{issuer.gstin ? `GSTIN: ${issuer.gstin}` : 'GSTIN: 24AAAAA0000A1Z5'}</span></div>
                      <div className="document-preview__title"><strong>{activeTypeMeta?.label?.toUpperCase()}</strong><span># {String(numbering.number_format || '{prefix}/{year}/{seq}').replace('{prefix}', numbering.prefix || 'DOC').replace('{year}', '2026').replace('{month}', '09').replace('{seq}', '0001')}</span></div>
                    </div>
                    <div className="document-preview__rule" />
                    <div className="document-preview__meta">
                      <div><small>{activeType === 'QUOTATION' ? 'ISSUED TO' : 'BILL TO'}</small><strong>Sample Customer Pvt Ltd</strong><span>Ahmedabad, Gujarat</span><span>GSTIN: 24BBBBB1111B1Z6</span></div>
                      <div><small>ISSUE DATE</small><strong>{formatDate('2026-09-12', dateFormat)}</strong><small>SCOPE</small><span>{scopeLabel}</span></div>
                    </div>
                    <div className="document-preview__table">
                      <div className="document-preview__row is-head"><span>#</span><span>Item</span><span>Qty</span><span>Rate</span><span>Total</span></div>
                      <div className="document-preview__row"><span>1</span><span><b>Product / Service</b><small>Description under the item name</small></span><span>2</span><span>₹12,000<small>per pax</small></span><span>₹24,000</span></div>
                      <div className="document-preview__row"><span>2</span><span><b>Additional Item</b><small>Optional detail</small></span><span>1</span><span>₹8,000</span><span>₹8,000</span></div>
                    </div>
                    <div className="document-preview__totals"><span>Subtotal</span><strong>₹32,000</strong><span>Discount</span><strong>₹2,000</strong>{activeType === 'TAX_INVOICE' && form.show_tax_breakdown ? <><span>CGST</span><strong>₹2,700</strong><span>SGST</span><strong>₹2,700</strong></> : null}<span className="is-grand">Total</span><strong className="is-grand">{activeType === 'PROFORMA_INVOICE' ? '₹30,000' : activeType === 'TAX_INVOICE' ? '₹35,400' : '₹30,000'}</strong></div>
                    {previewPages[previewPage]?.includeRichContent && form.payment_instructions ? <div className="document-preview__terms"><small>PAYMENT INSTRUCTIONS</small><div dangerouslySetInnerHTML={{ __html: form.payment_instructions }} /></div> : null}
                    {previewPages[previewPage]?.includeRichContent && form.terms_and_conditions ? <div className="document-preview__terms"><small>TERMS & CONDITIONS</small><div dangerouslySetInnerHTML={{ __html: form.terms_and_conditions }} /></div> : null}
                    <div className="document-preview__footer">{previewPages[previewPage]?.includeRichContent && form.notes ? <span dangerouslySetInnerHTML={{ __html: form.notes }} /> : (form.website || 'Thank you for your business.')}</div>
                  </>}
                  {form.show_page_numbers ? <div className="document-preview__page-number">Page {previewPage + 1} of {previewPages.length}</div> : null}
                </div>
                </>}
                <div className="document-template-settings__resolution-note">
                  <strong>Resolution</strong>
                  <span>Storefront → Business Unit → Brand → Legal Entity → Global. For a storefront-specific template, select that Storefront. For CRM documents, leave Storefront blank and use the appropriate Legal Entity / Brand / Business Unit or Global default. Priority breaks ties within the same scope.</span>
                </div>
              </div>
            </aside>
          </div>
        </main>
      </div>

      <ConfirmDialog
        open={deleteOpen}
        title="Delete document template?"
        message="This removes the template profile. Existing finalized document snapshots remain unchanged."
        confirmText="Delete Template"
        loading={deleting}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={remove}
      />
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification((current) => ({ ...current, open: false }))}
      />
    </div>
  );
}
